import { adminMonitoringService } from '@/services/admin-monitoring.service';
import type { AdminFilters } from '@/types/admin.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export function useServiceHealth() {
  return useQuery({
    queryKey: ['admin-health'],
    queryFn: () => adminMonitoringService.getHealth(),
    refetchInterval: 30000,
  });
}

export function useSystemMetrics() {
  return useQuery({
    queryKey: ['admin-metrics'],
    queryFn: () => adminMonitoringService.getMetrics(),
    refetchInterval: 15000,
  });
}

export function useAdminLogs(filters: AdminFilters & { level?: string; context?: string } = {}) {
  return useQuery({
    queryKey: ['admin-logs', filters],
    queryFn: () => adminMonitoringService.getLogs(filters),
  });
}

export function useBrowserPool() {
  return useQuery({
    queryKey: ['admin-browser-pool'],
    queryFn: () => adminMonitoringService.getBrowserPool(),
    refetchInterval: 10000,
  });
}

export function useRestartBrowserInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminMonitoringService.restartBrowserInstance(id),
    onSuccess: () => { toast.success('Instance redémarrée'); qc.invalidateQueries({ queryKey: ['admin-browser-pool'] }); },
    onError: () => toast.error('Erreur lors du redémarrage'),
  });
}

export function useCronJobs() {
  return useQuery({
    queryKey: ['admin-cron-jobs'],
    queryFn: () => adminMonitoringService.getCronJobs(),
    refetchInterval: 30000,
  });
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: () => adminMonitoringService.getDashboardStats(),
    refetchInterval: 30000,
  });
}

export function useActivityFeed() {
  return useQuery({
    queryKey: ['admin-activity-feed'],
    queryFn: () => adminMonitoringService.getActivityFeed(),
    refetchInterval: 30000,
  });
}

export function useSystemAlerts() {
  return useQuery({
    queryKey: ['admin-system-alerts'],
    queryFn: () => adminMonitoringService.getSystemAlerts(),
    refetchInterval: 30000,
  });
}

export function useApiLatency() {
  return useQuery({
    queryKey: ['admin-api-latency'],
    queryFn: () => adminMonitoringService.getApiLatency(),
    refetchInterval: 60000,
  });
}

export function useErrorRate() {
  return useQuery({
    queryKey: ['admin-error-rate'],
    queryFn: () => adminMonitoringService.getErrorRate(),
    refetchInterval: 60000,
  });
}
